import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';

import { NewUserAccountDetails } from '../../data';
import { useSxStyles } from '../../useStyles';

import { useNewUserAccount } from './useNewUserAccount';

interface Props {
  employeeId: number;
  open: boolean;
  newAccountDetails?: NewUserAccountDetails | undefined;
  showNewPassword?: boolean;
  hasWorkEmail: boolean;
  alternativeEmailEnabled: boolean;
  onClose: () => void;
}

export const NewUserAccountDialog = ({
  employeeId,
  newAccountDetails,
  showNewPassword,
  open,
  hasWorkEmail,
  alternativeEmailEnabled,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    form: { watch, control },
    onSubmit,
  } = useNewUserAccount(newAccountDetails || {}, employeeId, onClose);

  const sendSms = watch('sendSms', false);

  return (
    <DraggableDialog onClose={onClose} open={open}>
      <SolaForceDialogTitle onClose={onClose}>
        {t('laNewEmpInfo')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Box sx={sxStyles.formContent}>
          <Controller
            control={control}
            name={'username'}
            render={({ field: { value, onChange } }) => {
              return (
                <TextField
                  label={t('laLoginUsername')}
                  value={value}
                  onChange={onChange}
                  size={'small'}
                  inputProps={{ readOnly: true }}
                  fullWidth={true}
                />
              );
            }}
          />

          {showNewPassword && (
            <Controller
              control={control}
              name={'password'}
              render={({ field: { value, onChange } }) => {
                return (
                  <TextField
                    label={t('laPassword')}
                    value={value}
                    onChange={onChange}
                    size={'small'}
                    inputProps={{ readOnly: true }}
                    fullWidth={true}
                  />
                );
              }}
            />
          )}
        </Box>
        {!hasWorkEmail && alternativeEmailEnabled && (
          <>
            <Divider sx={sxStyles.divider} />
            <Box sx={sxStyles.formContent}>
              <Controller
                control={control}
                name={'sendSms'}
                render={({ field: { value, onChange } }) => {
                  return (
                    <FormControlLabel
                      control={<Checkbox checked={value} onChange={onChange} />}
                      label={t('laSendEmpInfoSms')}
                    />
                  );
                }}
              />

              <Box sx={sxStyles.formFieldWithAction}>
                <Controller
                  control={control}
                  name={'mobile'}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => {
                    return (
                      <TextField
                        label={t('laEnterMobile')}
                        value={value}
                        onChange={onChange}
                        size={'small'}
                        fullWidth={true}
                        error={!!error}
                        helperText={error?.message}
                        required={sendSms}
                      />
                    );
                  }}
                />
                {sendSms && (
                  <Button variant="contained" onClick={onSubmit}>
                    {t('laSend')}
                  </Button>
                )}
              </Box>

              <Box sx={sxStyles.formFieldWithAction}>
                <Controller
                  control={control}
                  name={'email'}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => {
                    return (
                      <TextField
                        label={t('laEmailAltEmail')}
                        size={'small'}
                        value={value}
                        onChange={onChange}
                        fullWidth={true}
                        error={!!error}
                        helperText={error?.message}
                        required={!sendSms}
                      />
                    );
                  }}
                />
                {!sendSms && (
                  <Button variant="contained" onClick={onSubmit}>
                    {t('laSend')}
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </DraggableDialog>
  );
};
